/**
 * ZoneVitalsCard — structured per-zone vitals table.
 *
 * Spec (founder request, May 2026):
 *   Record  : Date · Zone · EC · Moisture · Temp
 *   Aggregate per zone : Avg EC · Avg Moisture · Avg Temp (24h + 7d)
 *   Delta              : current vs 24h prior
 *   Actions            : per-zone, metric-specific recommendations
 *
 * Powered by GET /api/ecowitt/zone-vitals.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, TrendingUp, TrendingDown, Minus, Brain } from 'lucide-react';
import { API_URL } from '../config/api';

const BAND_COLORS = {
  // EC bands
  deficient: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  ideal:     'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  watch:     'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  high:      'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  // Moisture bands
  stressed:  'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  low:       'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  healthy:   'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  saturated: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  // Soil temp bands
  cool:      'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  optimal:   'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  hot:       'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  critical:  'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  unknown:   'bg-muted text-muted-foreground',
};

const SEVERITY_BORDER = {
  low:    'border-l-sky-400',
  medium: 'border-l-amber-400',
  high:   'border-l-rose-500',
};

// Decision Engine v1 — status & recommendation styling
const STATUS_PILL = {
  GOOD:  'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  WATCH: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  DRY:   'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
};
const REC_PILL = {
  SKIP:    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  WATER:   'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  MONITOR: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
};

const fmt = (n, suffix = '') => (n === null || n === undefined) ? '—' : `${typeof n === 'number' ? (Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1)) : n}${suffix}`;
const fmtDelta = (n, suffix = '') => {
  if (n === null || n === undefined) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${typeof n === 'number' ? (Math.abs(n) >= 100 ? n.toFixed(0) : n.toFixed(1)) : n}${suffix}`;
};

const DeltaIcon = ({ value }) => {
  if (value === null || value === undefined || Math.abs(value) < 0.1) return <Minus size={11} className="text-muted-foreground" />;
  return value > 0
    ? <TrendingUp size={11} className="text-amber-600 dark:text-amber-400" />
    : <TrendingDown size={11} className="text-sky-600 dark:text-sky-400" />;
};

const ZoneVitalsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ecowitt/zone-vitals`, { withCredentials: true });
      setData(r.data);
      setError(false);
    } catch (e) {
      console.error('Zone vitals fetch failed:', e?.response?.status, e?.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card data-testid="zone-vitals-card-loading">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading zone vitals…</CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card data-testid="zone-vitals-card-error">
        <CardContent className="p-6 text-sm text-rose-600 dark:text-rose-400">Could not load zone vitals.</CardContent>
      </Card>
    );
  }

  const zones = data?.zones || [];

  return (
    <Card data-testid="zone-vitals-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity size={16} className="text-emerald-600 dark:text-emerald-400" />
            Zone Vitals
          </CardTitle>
          {data?.as_of && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(data.as_of).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {data?.warning && (
          <p className="text-sm text-muted-foreground px-6 pb-4" data-testid="zone-vitals-warning">{data.warning}</p>
        )}

        {zones.length === 0 && !data?.warning && (
          <p className="text-sm text-muted-foreground px-6 pb-4">No active soil probes reporting yet.</p>
        )}

        {zones.length > 0 && (
          <>
            {/* Vitals table */}
            <div className="overflow-x-auto px-2 pb-2">
              <table className="w-full text-xs" data-testid="zone-vitals-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                    <th className="text-left px-2 py-2">Zone</th>
                    <th className="text-right px-2 py-2">EC<br/><span className="font-normal normal-case">µS/cm</span></th>
                    <th className="text-right px-2 py-2">Moisture<br/><span className="font-normal normal-case">%</span></th>
                    <th className="text-right px-2 py-2">Temp<br/><span className="font-normal normal-case">°F</span></th>
                    <th className="text-right px-2 py-2">Δ 24h<br/><span className="font-normal normal-case">M / T / EC</span></th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map(z => (
                    <React.Fragment key={z.channel}>
                    <tr className="border-b last:border-0" data-testid={`zone-row-${z.channel}`}>
                      <td className="px-2 py-2 align-top">
                        <div className="font-semibold">{z.zone_label}</div>
                        <div className="text-[9px] text-muted-foreground uppercase">CH{z.channel} · {z.sensor_model}</div>
                      </td>
                      <td className="text-right px-2 py-2 align-top">
                        <div className="font-mono">{fmt(z.current.ec)}</div>
                        <Badge className={`text-[9px] mt-0.5 ${BAND_COLORS[z.ec_band] || ''}`} variant="outline">{z.ec_band}</Badge>
                        {z.avg_24h.ec !== null && z.avg_7d.ec !== null && (
                          <div className="text-[9px] text-muted-foreground mt-0.5">
                            24h {fmt(z.avg_24h.ec)} · 7d {fmt(z.avg_7d.ec)}
                          </div>
                        )}
                      </td>
                      <td className="text-right px-2 py-2 align-top">
                        <div className="font-mono">{fmt(z.current.moisture)}</div>
                        <Badge className={`text-[9px] mt-0.5 ${BAND_COLORS[z.moisture_band] || ''}`} variant="outline">{z.moisture_band}</Badge>
                        {z.avg_24h.moisture !== null && z.avg_7d.moisture !== null && (
                          <div className="text-[9px] text-muted-foreground mt-0.5">
                            24h {fmt(z.avg_24h.moisture)} · 7d {fmt(z.avg_7d.moisture)}
                          </div>
                        )}
                      </td>
                      <td className="text-right px-2 py-2 align-top">
                        <div className="font-mono">{fmt(z.current.temp)}</div>
                        <Badge className={`text-[9px] mt-0.5 ${BAND_COLORS[z.temp_band] || ''}`} variant="outline">{z.temp_band}</Badge>
                        {z.avg_24h.temp !== null && z.avg_7d.temp !== null && (
                          <div className="text-[9px] text-muted-foreground mt-0.5">
                            24h {fmt(z.avg_24h.temp)} · 7d {fmt(z.avg_7d.temp)}
                          </div>
                        )}
                      </td>
                      <td className="text-right px-2 py-2 align-top">
                        <div className="flex items-center justify-end gap-1 font-mono">
                          <DeltaIcon value={z.delta_24h.moisture} /> {fmtDelta(z.delta_24h.moisture, '%')}
                        </div>
                        <div className="flex items-center justify-end gap-1 font-mono">
                          <DeltaIcon value={z.delta_24h.temp} /> {fmtDelta(z.delta_24h.temp, '°')}
                        </div>
                        <div className="flex items-center justify-end gap-1 font-mono">
                          <DeltaIcon value={z.delta_24h.ec} /> {fmtDelta(z.delta_24h.ec)}
                        </div>
                      </td>
                    </tr>
                    {z.decision && (
                      <tr className="border-b last:border-0 bg-muted/20" data-testid={`zone-decision-${z.channel}`}>
                        <td colSpan={5} className="px-2 pb-2 pt-1">
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <Brain size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                            <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Engine</span>
                            <Badge className={`text-[10px] font-bold ${STATUS_PILL[z.decision.status] || ''}`} variant="outline" data-testid={`decision-status-${z.channel}`}>
                              {z.decision.status}
                            </Badge>
                            <Badge className={`text-[10px] font-bold ${REC_PILL[z.decision.recommendation] || ''}`} variant="outline" data-testid={`decision-rec-${z.channel}`}>
                              {z.decision.recommendation}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground italic flex-1 min-w-0 truncate" title={z.decision.reason}>
                              {z.decision.reason}
                            </span>
                            {z.decision.rules_fired?.length > 0 && (
                              <span className="text-[9px] text-muted-foreground font-mono">
                                R{z.decision.rules_fired.join(',R')}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions per zone */}
            {zones.some(z => z.actions.length > 0) && (
              <div className="px-4 pb-4 pt-1 space-y-2" data-testid="zone-vitals-actions">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Recommended actions</div>
                {zones.flatMap(z => z.actions.map((a, idx) => (
                  <div
                    key={`${z.channel}-${idx}`}
                    className={`border-l-2 ${SEVERITY_BORDER[a.severity] || 'border-l-muted'} bg-muted/40 px-3 py-2 rounded-r`}
                    data-testid={`action-${z.channel}-${a.category}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase font-bold text-muted-foreground">{a.severity}</span>
                      <span className="font-semibold text-xs">{a.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.recommendation}</p>
                  </div>
                )))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ZoneVitalsCard;
